// components/CheckoutModal.js
import React, { useState } from 'react';
import Lottie from 'lottie-react';
import confettiAnimation from '../assets/lottie/confetti.json';

const CheckoutModal = ({
  isOpen,
  onClose,
  selectedCount,
  selectedTotal,
  totalWithDelivery,
  deliveryType,
  paymentMethod,
  deliveryCost,
  isSubmitting,
  showBankDetails,
  showSuccessOverlay,
  // Формы
  fullNameInput,
  setFullNameInput,
  email,
  setEmail,
  phone,
  setPhone,
  birthDate,
  setBirthDate,
  street,
  setStreet,
  deliveryDate,
  setDeliveryDate,
  deliveryTime,
  setDeliveryTime,
  deliveryInstructions,
  setDeliveryInstructions,
  setDeliveryType,
  setPaymentMethod,
  // Обработчики
  handleCheckout,
  handleCloseBankDetails,
  setShowBankDetails, // Добавляем проп
  navigate
}) => {
  // Если модалка закрыта - не рендерим
  if (!isOpen && !showBankDetails && !showSuccessOverlay) return null;

  // Функция для генерации опций дней текущего месяца
const generateDayOptions = () => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();
  
  // Получаем последний день текущего месяца
  const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  
  // Получаем текущий день
  const currentDay = today.getDate();
  
  const options = [];
  
  // Для оставшихся дней текущего месяца
  for (let day = currentDay; day <= lastDayOfMonth; day++) {
    const date = new Date(currentYear, currentMonth, day);
    const dateString = date.toISOString().split('T')[0];
    
    // Форматируем отображение даты
    const formattedDate = formatDeliveryDate(date);
    
    options.push(
      <option key={day} value={dateString}>
        {formattedDate}
      </option>
    );
  }
  
  // Если это конец месяца (осталось меньше 7 дней), добавляем дни следующего месяца
  const daysLeftInMonth = lastDayOfMonth - currentDay + 1;
  if (daysLeftInMonth < 7) {
    const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
    const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;
    
    // Добавляем первые дни следующего месяца, чтобы всего было минимум 7 опций
    const daysToAdd = 7 - daysLeftInMonth;
    
    for (let day = 1; day <= daysToAdd; day++) {
      const date = new Date(nextYear, nextMonth, day);
      const dateString = date.toISOString().split('T')[0];
      const formattedDate = formatDeliveryDate(date);
      
      options.push(
        <option key={`next-${day}`} value={dateString}>
          {formattedDate}
        </option>
      );
    }
  }
  
  return options;
};

// Функция для форматирования даты в читаемый вид
const formatDeliveryDate = (date) => {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const dayNames = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'];
  const monthNames = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
  
  const day = date.getDate();
  const dayOfWeek = dayNames[date.getDay()];
  const month = monthNames[date.getMonth()];
  
  // Проверяем, сегодня ли это
  if (date.toDateString() === today.toDateString()) {
    return `Сегодня, ${day} ${month}`;
  }
  
  // Проверяем, завтра ли это
  if (date.toDateString() === tomorrow.toDateString()) {
    return `Завтра, ${day} ${month}`;
  }
  
  // Для остальных дней
  return `${day} ${month}, ${dayOfWeek}`;
};

  // Функция для склонения товаров
  const formatItemCount = (count) => {
    const lastDigit = count % 10;
    const lastTwoDigits = count % 100;
    
    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
      return `${count} товаров`;
    }
    
    if (lastDigit === 1) {
      return `${count} товар`;
    }
    
    if (lastDigit >= 2 && lastDigit <= 4) {
      return `${count} товара`;
    }
    
    return `${count} товаров`;
  };

  // Формируем безопасный текст для Telegram
  const sanitizeText = (text) => text ? String(text).replace(/[*_`[\]()~>#+=|{}.!]/g, '') : '—';

  // Модалка с реквизитами
  if (showBankDetails) {
    return (
      <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60] p-2 animate-fadeIn">
        <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-hidden shadow-2xl animate-scaleIn flex flex-col">
          {/* Хедер - закреплен сверху */}
          <div className="bg-gradient-to-r from-blue-600 to-cyan-500 px-4 py-3 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-bold text-white font-Syne truncate">Реквизиты для оплаты</h3>
                  <p className="text-white/90 text-xs font-Inter truncate">Сохраните данные для перевода</p>
                </div>
              </div>
              <button 
                onClick={() => setShowBankDetails(false)} // Используем проп
                className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-all active:scale-95 flex-shrink-0 ml-2"
              >
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          
          {/* Контент - скроллится */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Важная информация */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <svg className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div>
                  <h4 className="font-semibold text-yellow-800 text-xs mb-1">Важная информация</h4>
                  <p className="text-yellow-700 text-xs leading-tight">
                    Заказ подтверждается после оплаты. Сохраните чек. Зачисление - до 24 часов.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Банковские реквизиты */}
            <div className="space-y-3">
              <h4 className="font-bold text-gray-900 text-sm mb-2 flex items-center gap-1">
                <svg className="w-3 h-3 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                  <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                </svg>
                Банковские реквизиты
              </h4>
              
              <div className="space-y-2">
                {/* Номер карты */}
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-gray-600 text-xs">Номер карты</span>
                    <button 
                      onClick={() => {
                        navigator.clipboard.writeText('2200 7000 0000 0000');
                        alert('Номер карты скопирован');
                      }}
                      className="text-blue-500 hover:text-blue-700 text-xs flex items-center gap-1"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                  <div className="font-mono text-sm font-bold text-gray-900 tracking-wider break-all">2200 7000 0000 0000</div>
                </div>
                
                {/* Получатель */}
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-gray-600 text-xs">Получатель</span>
                    <button 
                      onClick={() => {
                        navigator.clipboard.writeText('Иванов Иван Иванович');
                        alert('Имя получателя скопировано');
                      }}
                      className="text-blue-500 hover:text-blue-700 text-xs flex items-center gap-1"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                  <div className="font-medium text-gray-900 text-sm">Иванов Иван Иванович</div>
                </div>
                
                {/* Банк */}
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-gray-600 text-xs">Банк</span>
                    <button 
                      onClick={() => {
                        navigator.clipboard.writeText('Тинькофф Банк');
                        alert('Название банка скопировано');
                      }}
                      className="text-blue-500 hover:text-blue-700 text-xs flex items-center gap-1"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                  <div className="font-medium text-gray-900 text-sm">Тинькофф Банк</div>
                </div>
                
                {/* Сумма */}
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                  <div className="mb-1">
                    <span className="text-gray-600 text-xs">Сумма к оплате</span>
                  </div>
                  <div className="font-bold text-lg text-blue-600">{totalWithDelivery.toLocaleString()} ₽</div>
                </div>
              </div>
            </div>
            
            {/* Инструкция по оплате */}
            <div>
              <h4 className="font-bold text-gray-900 text-sm mb-2 flex items-center gap-1">
                <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Инструкция по оплате
              </h4>
              
              <div className="space-y-1">
                {[
                  "Скопируйте номер карты получателя",
                  "Откройте приложение вашего банка",
                  "Выполните перевод на указанные реквизиты",
                  "Сохраните чек об оплате",
                  "Ожидайте подтверждения заказа"
                ].map((step, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <div className="w-4 h-4 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-blue-600 text-[10px] font-bold">{index + 1}</span>
                    </div>
                    <p className="text-gray-700 text-xs flex-1">{step}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Дополнительная информация */}
            <div className="pt-2 border-t border-gray-200">
              <p className="text-gray-600 text-xs mb-3">
                После оплаты пришлите скриншот чека в поддержку для ускорения обработки заказа.
              </p>
            </div>
          </div>
          
          {/* Кнопки - закреплены снизу */}
          <div className="p-4 border-t border-gray-200 bg-white flex-shrink-0">
            <div className="flex flex-col gap-2">
              <button
                onClick={handleCloseBankDetails}
                className="w-full py-2.5 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold text-sm hover:from-green-600 hover:to-emerald-600 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                Сохранил(а) реквизиты
              </button>
              
              <button
                onClick={() => setShowBankDetails(false)} // Используем проп
                className="w-full py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-all font-medium text-xs"
              >
                Вернуться к заказу
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success Overlay
  if (showSuccessOverlay) {
    return (
      <div className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center text-white p-4">
        <div className="bg-black rounded-3xl p-8 shadow-2xl transform animate-scaleIn">
          <Lottie
            animationData={confettiAnimation}
            loop={false}
            style={{ width: 200, height: 200 }}
          />
          <div className="text-center">
            <div className="text-3xl text-white font-bold mb-2">🎉 Заказ оформлен!</div>
            <p className="text-lg text-white opacity-90">Номер заказа: #{Math.random().toString(36).substr(2, 8).toUpperCase()}</p>
            <p className="mt-4 text-white opacity-80">Мы свяжемся с вами в ближайшее время</p>
          </div>
        </div>
      </div>
    );
  }

  // Основное модальное окно оформления заказа
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 animate-fadeIn">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[95vh] overflow-y-auto shadow-2xl animate-scaleIn">
        
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white">
          <div className="bg-gradient-to-r from-blue-600 to-cyan-500 px-4 py-4 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-bold text-white font-Syne truncate">Оформление заказа</h3>
                  <p className="text-white/90 text-xs font-Inter">Заполните данные для доставки</p>
                </div>
              </div>
              <button 
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-all active:scale-95 flex-shrink-0"
              >
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          
          {/* Мини-сводка */}
          <div className="px-4 py-3 border-b border-borders2">
            <div className="flex justify-between items-center font-PublicSans text-sm">
              <div className="text-gray-600">
                <span className="font-medium text-gray-900">{selectedCount}</span> 
                <span className="ml-1">
                  {selectedCount === 1 ? 'товар' : 
                   selectedCount > 1 && selectedCount < 5 ? 'товара' : 
                   'товаров'}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-gray-600 text-xs">Сумма:</span>
                <span className="text-lg font-bold text-gray-900 font-Syne">{selectedTotal.toLocaleString()} ₽</span>
              </div>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="p-4 space-y-4 font-Inter">
          
          {/* Контактные данные */}
          <div className="space-y-3">
            <h4 className="text-base font-semibold text-gray-900 flex items-center gap-2 font-Syne">
              <div className="w-6 h-6 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                1
              </div>
              Контактные данные
            </h4>
            
            <div className="space-y-3">
              {/* Full Name */}
              <div>
                <label className="block text-gray-700 font-medium mb-1 text-xs font-PublicSans">ФИО *</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    className="w-full pl-10 pr-3 py-3 border border-borders2 bg-gray-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent text-sm placeholder:text-secondary"
                    value={fullNameInput}
                    onChange={(e) => setFullNameInput(e.target.value)}
                    placeholder="Иванов Иван Иванович"
                    required
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-gray-700 font-medium mb-1 text-xs font-PublicSans">Телефон *</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <input
                    type="tel"
                    className="w-full pl-10 pr-3 py-3 border border-borders2 bg-gray-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent text-sm placeholder:text-secondary"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+7 (999) 999-99-99"
                    required
                  />
                </div>
                {phone && !/^\+?\d{7,15}$/.test(phone) && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    Введите корректный номер
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-gray-700 font-medium mb-1 text-xs font-PublicSans">Email</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <input
                    type="email"
                    className="w-full pl-10 pr-3 py-3 border border-borders2 bg-gray-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent text-sm placeholder:text-secondary"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="example@mail.com"
                  />
                </div>
              </div>

              {/* День рождения */}
              <div>
                <label className="block text-gray-700 font-medium mb-1 text-xs font-PublicSans">День рождения</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <input
                    type="date"
                    className="w-full pl-10 pr-3 py-3 border border-borders2 bg-gray-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent text-sm placeholder:text-secondary"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                  />
                </div>
                <p className="text-gray-500 text-xs mt-1">Укажите для получения персональных предложений</p>
              </div>
            </div>
          </div>

         {/* Адрес и время доставки */}
<div className="space-y-3">
  <h4 className="text-base font-semibold text-gray-900 flex items-center gap-2 font-Syne">
    <div className="w-6 h-6 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
      2
    </div>
    Адрес и время доставки
  </h4>
  
  <div className="space-y-3">
    {/* Дата и время в одной строке */}
    <div className="grid grid-cols-2 gap-2">
      <div>
        <label className="block text-gray-600 text-xs mb-1 font-medium">Дата *</label>
        <div className="relative">
          <div className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          {/* Заменяем обычный input на кастомный селект */}
          <select
            className="w-full pl-7 pr-2 py-2.5 border border-borders2 bg-gray-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent text-xs appearance-none"
            value={deliveryDate || ''}
            onChange={(e) => setDeliveryDate(e.target.value)}
            required
          >
            <option value="">Выберите день</option>
            {generateDayOptions()}
          </select>
          {/* Стрелка для селекта */}
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>
      
      <div>
        <label className="block text-gray-600 text-xs mb-1 font-medium">Время *</label>
        <div className="relative">
          <div className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <input
            type="time"
            className="w-full pl-7 pr-2 py-2.5 border border-borders2 bg-gray-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent text-xs"
            value={deliveryTime || ''}
            onChange={(e) => setDeliveryTime(e.target.value)}
            required
          />
        </div>
      </div>
    </div>


              
              {/* Улица */}
              <div>
                <label className="block text-gray-600 text-xs mb-1 font-medium">Адрес *</label>
                <div className="relative">
                  <div className="absolute left-2 top-3 text-gray-400">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    className="w-full pl-7 pr-3 py-2.5 border border-borders2 bg-gray-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent text-sm"
                    value={street}
                    onChange={(e) => setStreet(e.target.value)}
                    placeholder="ул. Пушкина, д. 10, кв. 5"
                    required
                  />
                </div>
              </div>
              
              {/* Как пройти */}
              <div>
                <label className="block text-gray-600 text-xs mb-1 font-medium">Как пройти (для курьера)</label>
                <div className="relative">
                  <div className="absolute left-2 top-2 text-gray-400">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <textarea
                    className="w-full pl-7 pr-3 py-2.5 border border-borders2 bg-gray-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent text-sm min-h-[60px]"
                    value={deliveryInstructions}
                    onChange={(e) => setDeliveryInstructions(e.target.value)}
                    placeholder="Код домофона, подъезд, этаж"
                    rows="2"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Тип доставки */}
          <div className="space-y-3">
            <h4 className="text-base font-semibold text-gray-900 flex items-center gap-2 font-Syne">
              <div className="w-6 h-6 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                3
              </div>
              Тип доставки
            </h4>
            
            <div className="space-y-2">
              {/* Доставка внутри МКАД */}
              <label className={`flex items-center justify-between p-3 border-2 rounded-lg cursor-pointer transition-all
                ${deliveryType === 'mkad' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-borders2 bg-cards2 hover:border-borders'
                }`}>
                <input
                  type="radio"
                  name="deliveryType"
                  value="mkad"
                  checked={deliveryType === 'mkad'}
                  onChange={() => setDeliveryType('mkad')}
                  className="hidden"
                />
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 border-2 rounded-full flex items-center justify-center ${deliveryType === 'mkad' ? 'border-blue-500' : 'border-gray-300'}`}>
                    {deliveryType === 'mkad' && <div className="w-2 h-2 bg-blue-500 rounded-full"></div>}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 text-sm font-Syne">Внутри МКАД</div>
                    <div className="text-xs text-gray-500">Быстрая доставка</div>
                  </div>
                </div>
                <div className="font-bold text-gray-900 text-sm">800 ₽</div>
              </label>

              {/* Доставка за МКАД */}
              <label className={`flex items-center justify-between p-3 border-2 rounded-lg cursor-pointer transition-all
                ${deliveryType === 'outside_mkad' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-borders2 bg-cards2 hover:border-borders'
                }`}>
                <input
                  type="radio"
                  name="deliveryType"
                  value="outside_mkad"
                  checked={deliveryType === 'outside_mkad'}
                  onChange={() => setDeliveryType('outside_mkad')}
                  className="hidden"
                />
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 border-2 rounded-full flex items-center justify-center ${deliveryType === 'outside_mkad' ? 'border-blue-500' : 'border-gray-300'}`}>
                    {deliveryType === 'outside_mkad' && <div className="w-2 h-2 bg-blue-500 rounded-full"></div>}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 text-sm font-Syne">За МКАД до 10км</div>
                    <div className="text-xs text-gray-500">Расширенная зона</div>
                  </div>
                </div>
                <div className="font-bold text-gray-900 text-sm">1 300 ₽</div>
              </label>
            </div>
          </div>

          {/* Способ оплаты */}
          <div className="space-y-3">
            <h4 className="text-base font-semibold text-gray-900 flex items-center gap-2 font-Syne">
              <div className="w-6 h-6 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                4
              </div>
              Способ оплаты
            </h4>
            
            <div className="grid grid-cols-2 gap-2">
              {/* Наличными */}
              <label className={`p-3 border-2 rounded-lg cursor-pointer transition-all text-center
                ${paymentMethod === 'cash' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-borders2 bg-cards2 hover:border-borders'
                }`}>
                <input
                  type="radio"
                  name="paymentMethod"
                  value="cash"
                  checked={paymentMethod === 'cash'}
                  onChange={() => setPaymentMethod('cash')}
                  className="hidden"
                />
                <div className="text-lg mb-1">💵</div>
                <div className="font-medium text-gray-900 text-xs font-Syne">Наличными</div>
                <div className="text-xs text-gray-500 mt-0.5">курьеру</div>
              </label>

              {/* Переводом */}
              <label className={`p-3 border-2 rounded-lg cursor-pointer transition-all text-center
                ${paymentMethod === 'bank_transfer' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-borders2 bg-cards2 hover:border-borders'
                }`}>
                <input
                  type="radio"
                  name="paymentMethod"
                  value="bank_transfer"
                  checked={paymentMethod === 'bank_transfer'}
                  onChange={() => setPaymentMethod('bank_transfer')}
                  className="hidden"
                />
                <div className="text-lg mb-1">🏦</div>
                <div className="font-medium text-gray-900 text-xs font-Syne">Переводом</div>
                <div className="text-xs text-gray-500 mt-0.5">на карту</div>
              </label>
            </div>
            
            {paymentMethod === 'bank_transfer' && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-blue-700 text-xs flex items-start gap-2">
                  <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <span>
                    Реквизиты для перевода будут показаны после оформления заказа. 
                    Пожалуйста, сохраните их для совершения оплаты.
                  </span>
                </p>
              </div>
            )}
          </div>

          {/* Итоговая информация */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-xl border border-borders2">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Товары ({selectedCount})</span>
                <span className="font-medium">{selectedTotal.toLocaleString()} ₽</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600">
                  {deliveryType === 'mkad' ? 'Доставка (МКАД)' : 
                   deliveryType === 'outside_mkad' ? 'Доставка (за МКАД)' : 
                   'Доставка'}
                </span>
                <span className="font-medium">
                  {deliveryType === 'mkad' ? '800 ₽' : 
                   deliveryType === 'outside_mkad' ? '1 300 ₽' : 
                   '—'}
                </span>
              </div>
              
              <div className="pt-2 border-t border-divider3">
                <div className="flex justify-between font-bold">
                  <span>Итого к оплате</span>
                  <span className="text-blue-600 text-base">{totalWithDelivery.toLocaleString()} ₽</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {paymentMethod === 'cash' 
                    ? 'Оплата наличными курьеру' 
                    : 'Оплата по реквизитам'}
                </p>
              </div>
            </div>
          </div>

          {/* Кнопки */}
          <div className="flex flex-col gap-2 pt-2">
            <button
              onClick={handleCheckout}
              disabled={
                isSubmitting ||
                !fullNameInput.trim() ||
                !phone ||
                !deliveryDate ||
                !deliveryTime ||
                !street ||
                !deliveryType ||
                !/^\+?\d{7,15}$/.test(phone)
              }
              className="w-full py-3 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold text-base hover:from-blue-700 hover:to-cyan-600 transition-all shadow-lg active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-Syne"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                  Оформляем...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Подтвердить заказ
                </>
              )}
            </button>
            
            <button
              onClick={onClose}
              className="w-full py-2.5 rounded-xl border border-borders2 text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-all font-medium text-sm active:scale-[0.98]"
            >
              Вернуться к корзине
            </button>
          </div>
          
          {/* Информация о защите */}
          <div className="text-center pt-2">
            <p className="text-[10px] text-gray-500 flex items-center justify-center gap-1">
              <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Ваши данные защищены
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutModal;